import { useState, useMemo, useEffect } from "react";
import {
  Compass,
  MapPin,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  X,
  Calendar,
  Info,
  Tag,
  Clock,
  Settings,
} from "lucide-react";
import { useAppContext } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigation } from "@/context/NavigationContext";
import InstrumentLoader from "@/components/InstrumentLoader";
import { message } from "antd";
import ExperienceAvailabilityModal from "@/components/ExperienceAvailabilityModal";
import { ExperienceDto, ExperienceDetailsDto } from "@/types";
import { EXPERIENCE_CATEGORIES } from "../enums";

const isInvalidValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  const str = String(value).trim().toLowerCase();
  return str === "" || str === "null" || str === "undefined" || str === "n/a" || str === "na";
};

const ExperienceDetails = () => {
  const { params, navigateTo } = useNavigation();
  const experienceId = params?.experienceId;
  const experienceFromParams = params?.experience as (ExperienceDto & Partial<ExperienceDetailsDto>) | undefined;
  const seller = params?.seller;

  const { experiences, loading, getExperienceDetails } = useAppContext();
  const { isAuthenticated, myExperiences } = useAuth();

  const [details, setDetails] = useState<ExperienceDetailsDto | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(true);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const isSeller = useMemo(() => {
    const isSellerParam = Number(seller) === 1 || seller === "true" || seller === true;

    const targetId = experienceId || experienceFromParams?.experienceId;
    const isInMyExperiences = myExperiences && targetId
      ? myExperiences.some((myExp: any) => Number(myExp.experienceId) === Number(targetId))
      : false;

    return !!(isSellerParam || isInMyExperiences);
  }, [seller, experienceId, experienceFromParams, myExperiences]);

  const baseExperience = useMemo(() => {
    if (experienceFromParams) return experienceFromParams;
    if (!experienceId) return null;
    return (experiences || []).find(e => Number(e.experienceId) === Number(experienceId)) || null;
  }, [experiences, experienceId, experienceFromParams]);

  useEffect(() => {
    const targetId = Number(experienceId || baseExperience?.experienceId);
    if (!targetId) {
      setIsLoadingDetails(false);
      return;
    }

    let isMounted = true;
    setIsLoadingDetails(true);

    getExperienceDetails(targetId)
      .then((data) => {
        if (isMounted && data) {
          setDetails(data);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingDetails(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [experienceId, baseExperience?.experienceId, getExperienceDetails]);

  const experience = useMemo(() => {
    if (!baseExperience) return null;

    const merged = {
      ...baseExperience,
      ...(details || {})
    };

    const categoryObj = EXPERIENCE_CATEGORIES.find((cat) => cat.code === merged.type);
    const resolvedTypeLabel = categoryObj ? categoryObj.label : merged.type;

    return {
      ...merged,
      resolvedTypeLabel
    };
  }, [baseExperience, details]);

  const images = useMemo(() => {
    if (experience?.mediaUrls && experience.mediaUrls.length > 0) {
      return experience.mediaUrls.filter(url => url && url.trim() !== "");
    }

    if (experience?.media && experience.media.length > 0) {
      return experience.media.map((m: any) => {
        const data = m.mediaData;
        if (!data) return "";
        return data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`;
      }).filter((img: string) => img !== "");
    }

    return [`https://via.placeholder.com/1200x800?text=${encodeURIComponent(experience?.title || "Experience")}`];
  }, [experience]);

  const isPageLoading = (loading && !experienceFromParams) || isLoadingDetails;

  if (isPageLoading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <InstrumentLoader visible={true} />
      </div>
    );
  }

  if (!experience) {
    return (
      <div className="text-white text-center py-20 font-sans bg-[#020617] min-h-screen">
        <p className="mb-4 text-slate-500 uppercase tracking-widest font-black">Experiența nu a fost găsită</p>
        <button onClick={() => navigateTo("experiences")} className="text-red-500 underline font-bold uppercase text-xs tracking-wider">
          Înapoi la Catalog
        </button>
      </div>
    );
  }

  const handleAction = () => {
    if (isSeller) {
      navigateTo("list-experience", { experience: experience });
      return;
    }

    if (!isAuthenticated) {
      message.warning("Autentificarea este necesară pentru a rezerva o experiență.");
      navigateTo("login");
      return;
    }
    setIsBookingModalOpen(true);
  };

  const formatPrice = (price?: number) => {
    if (!price || isInvalidValue(price) || price === 0) return "LA CERERE";
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0
    }).format(price);
  };

  const DetailRow = ({ label, value, icon: Icon }: { label: string; value?: string | number | boolean | null; icon?: any }) => {
    if (typeof value !== "boolean" && isInvalidValue(value)) return null;

    return (
      <div className="flex justify-between items-center py-3 border-b border-white/5 group hover:bg-white/[0.02] px-2 transition-colors">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-slate-500" />}
          <span className="text-slate-400 text-[11px] font-bold uppercase tracking-wider">{label}</span>
        </div>
        <span className="text-white font-mono text-sm text-right uppercase">
          {typeof value === "boolean" ? (value ? "DA" : "NU") : value}
        </span>
      </div>
    );
  };

  const hasDescription = !isInvalidValue(experience.description);
  const hasDuration = !isInvalidValue(experience.durationInMinutes);
  const hasTimeInterval = !isInvalidValue(experience.timeInterval);

  return (
    <div className="container py-8 text-white min-h-screen lg:h-screen flex flex-col lg:overflow-hidden font-sans bg-[#020617]">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 flex-1 lg:overflow-hidden">
        {/* Left Column: Visual Gallery */}
        <div className="lg:col-span-7 h-auto lg:h-full lg:overflow-y-auto no-scrollbar pr-0 lg:pr-2">
          <div className="flex flex-row lg:flex-col rounded-[2rem] overflow-x-auto lg:overflow-hidden snap-x snap-mandatory no-scrollbar border border-white/10 bg-slate-900 shadow-2xl">
            {images.map((src, idx) => (
              <div
                key={idx}
                className="relative min-w-full lg:min-w-0 snap-center group cursor-zoom-in border-r lg:border-r-0 lg:border-b border-white/5 last:border-0"
                onClick={() => {
                  setPhotoIndex(idx);
                  setIsLightboxOpen(true);
                }}
              >
                <img
                  src={src}
                  className="w-full h-[350px] md:h-[500px] lg:h-auto object-cover transition-transform duration-500 lg:group-hover:scale-[1.01]"
                  alt={`Experience Gallery ${idx}`}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://placehold.co/1200x800?text=Imagine+Indisponibila';
                  }}
                />

                <div className="hidden lg:flex absolute inset-0 bg-red-600/10 opacity-0 group-hover:opacity-100 transition-opacity items-center justify-center">
                  <Maximize2 className="text-white" size={32} />
                </div>

                <div className="lg:hidden absolute bottom-4 right-6 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-mono border border-white/10 text-white">
                  {idx + 1} / {images.length}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Flight Data & Booking */}
        <div className="lg:col-span-5 h-auto lg:h-full lg:overflow-y-auto no-scrollbar pb-10 lg:pb-20">
          <div className="bg-[#0F172A]/80 backdrop-blur-xl p-8 rounded-[2.5rem] border border-slate-800 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/10 blur-3xl -z-10" />

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-0.5 text-[9px] font-black rounded uppercase tracking-tighter ${
                  experience.active ? "bg-emerald-600 text-white" : "bg-red-600 text-white"
                }`}>
                  {experience.active ? "Disponibil" : "Arhivat"}
                </span>
                <span className="text-slate-500 font-mono text-[10px] tracking-widest">
                  ID: #{experience.experienceId || experienceId || "DYNAMIC"}
                </span>
              </div>
              <h1 className="text-4xl font-black tracking-tight mb-1 uppercase italic text-white">
                {experience.title || "Experiență Aviatică"}
              </h1>
              {!isInvalidValue(experience.location) && (
                <p className="text-slate-400 text-sm font-bold uppercase tracking-widest flex items-center gap-2 mt-2">
                  <MapPin size={14} className="text-red-500" /> {experience.location}
                </p>
              )}
            </header>

            {/* Pricing Card */}
            <div className="bg-slate-950/50 border border-slate-800 p-6 rounded-2xl mb-8">
              <div className="flex justify-between items-end mb-4">
                <div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Tarif Experiență</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-white italic">
                      {formatPrice(experience.grossValue)}
                    </span>
                  </div>
                </div>
                {!isInvalidValue(experience.resolvedTypeLabel) && (
                  <div className="text-right">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Clasificare</p>
                    <span className="text-red-400 font-mono font-bold text-xs uppercase bg-red-500/10 px-2.5 py-1 rounded-md border border-red-500/20 tracking-wider">
                      {experience.resolvedTypeLabel}
                    </span>
                  </div>
                )}
              </div>

              {!(isSeller && experience.verified === false) && (
                <button
                  className="w-full py-4 bg-red-600 hover:bg-red-700 text-white font-black rounded-xl shadow-lg shadow-black/40 transition-all flex items-center justify-center gap-3 active:scale-[0.98] uppercase tracking-widest text-sm"
                  onClick={handleAction}
                >
                  {isSeller ? (
                    <>
                      <Settings size={18} /> Editează Experiența
                    </>
                  ) : (
                    <>
                      <Calendar size={18} /> Rezervă Experiența
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="space-y-10">
              {/* Tactical Overview */}
              <section>
                <h3 className="text-red-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                  <Compass size={14} className="animate-pulse" /> Detalii Operaționale
                </h3>
                <div className="space-y-1">
                  <DetailRow label="Locație Desfășurare" value={experience.location} icon={MapPin} />
                  <DetailRow label="Tip Activitate" value={experience.resolvedTypeLabel} icon={Tag} />
                  {hasDuration && (
                    <DetailRow label="Durată Totală" value={`${experience.durationInMinutes} min`} icon={Clock} />
                  )}
                  {hasTimeInterval && (
                    <DetailRow label="Interval Orar" value={experience.timeInterval} icon={Clock} />
                  )}
                  <DetailRow label="Rezervare Instantă" value={true} icon={Clock} />
                </div>
              </section>

              {/* Description Section */}
              {hasDescription && (
                <section className="pt-6 border-t border-slate-800">
                  <h3 className="text-red-500 text-[10px] uppercase tracking-[0.3em] font-black mb-3 flex items-center gap-2">
                    <Info size={14} /> Briefing & Descriere Completă
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed font-normal whitespace-pre-line">
                    {experience.description}
                  </p>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Structural Overlay */}
      {isLightboxOpen && (
        <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center animate-in fade-in duration-300" onClick={() => setIsLightboxOpen(false)}>
          <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center pointer-events-none">
            <span className="text-white font-mono text-sm pointer-events-auto">{photoIndex + 1} / {images.length}</span>
            <button className="p-3 hover:bg-white/10 rounded-full transition-colors pointer-events-auto" onClick={() => setIsLightboxOpen(false)}>
              <X size={32} />
            </button>
          </div>
          <div className="relative w-full h-full flex items-center justify-center p-4 md:p-12">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPhotoIndex((p) => (p === 0 ? images.length - 1 : p - 1));
              }}
              className="absolute left-4 md:left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-all active:scale-90 z-10"
            >
              <ChevronLeft size={40} />
            </button>

            <div
              className="w-full max-w-[85vw] aspect-video rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/10 bg-slate-900"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[photoIndex]}
                className="w-full h-full object-cover"
                alt="Expanded View"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://placehold.co/1200x800?text=Imagine+Indisponibila';
                }}
              />
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setPhotoIndex((p) => (p + 1) % images.length);
              }}
              className="absolute right-4 md:right-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-all active:scale-90 z-10"
            >
              <ChevronRight size={40} />
            </button>
          </div>
        </div>
      )}

      {/* 🟢 Trimitem corect experienceDetails în loc de bookings */}
      <ExperienceAvailabilityModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        registration={experience?.title || "Flight Experience"}
        hourlyRate={experience?.grossValue || 0}
        durationInMinutes={experience?.durationInMinutes}
        timeInterval={experience?.timeInterval}
        experienceId={Number(experienceId || experience?.experienceId)}
        experienceDetails={details || experience}
      />
    </div>
  );
};

export default ExperienceDetails;